import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Alert, Button, CircularProgress, Snackbar } from "@mui/material";
import { Image as ImageIcon } from "react-feather";
import { addToCart, belowMinimum, checkoutPath, formatPrice, getCart, isCustomer, removeFromCart } from "../shopApi";
import MinOrderNotice from "./MinOrderNotice";
import styles from "../shop.module.css";

/**
 * Cart lines and summary, for a guest (public /shop/cart) or a customer (/customer/shop/cart).
 * shopPath: where "Continue shopping" and item links go.
 */
const CartView = ({ shopPath }) => {
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [busyLine, setBusyLine] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    getCart().then((res) => setCart(res.data)).catch((e) => { setCart({ items: [], subtotal: 0, count: 0 }); setError(e.message); });
  }, []);

  const run = (lineId, request) => {
    setBusyLine(lineId);
    request.then((res) => { setCart(res.data); setBusyLine(0); }).catch((e) => { setBusyLine(0); setError(e.message); });
  };

  const setQty = (line, qty) => run(line.id, qty < 1 ? removeFromCart(line.id) : addToCart(line.product_id, qty, true));

  const blocked = cart?.items?.some((i) => i.unavailable);

  return (<>
    {!cart && <div className={styles.Empty}><CircularProgress size={28} /></div>}

    {cart && cart.items.length === 0 && (<div className={`${styles.Panel} ${styles.Empty}`}>
      <p>Your cart is empty.</p>
      <Button component={Link} to={shopPath} className={styles.PrimaryBU} style={{ marginTop: 12 }}>Shop Now</Button>
    </div>)}

    {cart && cart.items.length > 0 && (<div className={styles.TwoCol}>
      <div className={styles.Panel}>
        {cart.items.map((line) => (<div className={styles.CartLine} key={line.id}>
          {line.product.image ? <img src={line.product.image} alt="" className={styles.CartThumb} /> : <span className={`${styles.CartThumb} ${styles.NoImage}`}><ImageIcon size={22} /></span>}
          <div>
            <Link to={`${shopPath}/${line.product_id}`} className={styles.LineTitle}>{line.product.title}</Link>
            <div className={styles.LineMeta}>
              {line.product.on_sale && <s className={styles.WasPrice}>{formatPrice(line.product.compare_at_price)}</s>}
              {formatPrice(line.product.price)} each
            </div>
            {line.unavailable && <div className={styles.LineWarn}>{line.product.in_stock ? `Only ${line.product.max_qty} left. Please lower the quantity.` : "Sold out. Please remove it to continue."}</div>}
          </div>
          <div className={styles.LineRight}>
            <strong>{formatPrice(line.line_total)}</strong>
            <div className={styles.Qty}>
              <button type="button" disabled={busyLine === line.id} onClick={() => setQty(line, line.quantity - 1)} aria-label="Fewer">−</button>
              <span>{line.quantity}</span>
              <button type="button" disabled={busyLine === line.id || (line.product.max_qty !== null && line.quantity >= line.product.max_qty)} onClick={() => setQty(line, line.quantity + 1)} aria-label="More">+</button>
            </div>
            <button type="button" className={styles.RemoveBU} disabled={busyLine === line.id} onClick={() => run(line.id, removeFromCart(line.id))}>Remove</button>
          </div>
        </div>))}
      </div>

      <div className={styles.Panel}>
        <h2 className={styles.PanelTitle}>Order summary</h2>
        <div className={styles.SumRow}><span>Items ({cart.count})</span><span>{formatPrice(cart.subtotal)}</span></div>
        <div className={styles.SumRow}><span>Delivery</span><span>At checkout</span></div>
        <div className={`${styles.SumRow} ${styles.SumTotal}`}><span>Subtotal</span><span>{formatPrice(cart.subtotal)}</span></div>
        <MinOrderNotice cart={cart} />
        <Button fullWidth size="large" className={styles.PrimaryBU} style={{ marginTop: 14 }} disabled={blocked || belowMinimum(cart)} onClick={() => navigate(checkoutPath())}>Checkout</Button>
        {blocked && <span className={styles.Error}>Some items need your attention before checkout.</span>}
        {!isCustomer() && <span className={styles.Hint} style={{ textAlign: "center", marginTop: 8 }}>Sign in or create an account to check out. Your cart is saved.</span>}
      </div>
    </div>)}

    <Snackbar open={error !== ""} autoHideDuration={6000} onClose={() => setError("")} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
      <Alert severity="error" variant="filled" onClose={() => setError("")}>{error}</Alert>
    </Snackbar>
  </>);
};

export default CartView;
